import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { CoreBox, CoreTypography } from '@youscience/core';
import { classifications, Picture } from '@common-types';

import useHomePageStore from '@store/HomePageStore';

import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import { getSchoolDetailsPath } from '@constants/ROUTES';

import { getImageSrc } from '@utils/schoolPhoto';

import { Breadcrumbs } from '@components/Breadcrumbs';
import { IPEDS } from '@components/IPEDS';
import { Container } from '@components/Layout/components/Container';

import baseHomeImage from '@images/base-home-page.jpg';
import PlusIcon from '@images/plus.svg?react';
import PreviewIcon from '@images/preview.svg?react';
import ImageIcon from '@images/image.svg?react';
import EditImage from './components/EditImage';

const AreasAccordion = lazy(() => import('./components/AreasAccordion'));
const AreaModal = lazy(() => import('./components/AreaModal'));
const NotepadIcon = lazy(() => import('@images/notepad.svg?react'));

import {
  sxStyles,
  StyledHeroSection,
  StyledHeroText,
  StyledHeroImg,
  StyledHeaderActionsWrap,
  StyledAddAreaBtn,
  StyledPreviewBtn,
  StyledUpdateImageBtn,
  StyledEmptyStateText,
} from './AdminHomePage.styles';

export const AdminHomePage = () => {
  const {
    adminHomePageData: { ipedsId, school },
  } = useHomePageStore((state) => state);

  const [isOpenAreaModal, setIsOpenAreaModal] = useState(false);
  const [showEditPhoto, setShowEditPhoto] = useState(false);
  const [editModeCipCode, setEditModeCipCode] = useState<number | null>(null);
  const [schoolPhotoUrl, setSchoolPhotoUrl] = useState<string | null>(null);

  const areas = useMemo(() => {
    const areasCipCodes = school.completions?.map((area) => Math.floor(+area.cipCode));

    return areasCipCodes?.filter((el, index) => areasCipCodes.indexOf(el) === index) || [];
  }, [school.completions]);

  useEffect(() => {
    setSchoolPhotoUrl(getImageSrc(ipedsId, baseHomeImage, school?.picture as Picture));
  }, [ipedsId, school]);

  const handleOpenAreaModal = (areaCipCode?: number) => () => {
    setIsOpenAreaModal(true);

    if (areaCipCode) {
      setEditModeCipCode(areaCipCode);
    }
  };

  const handleCloseAreaModal = () => {
    setIsOpenAreaModal(false);
    setEditModeCipCode(null);
  };

  return (
    <>
      <CoreBox sx={sxStyles.heroWrap}>
        <StyledHeroSection>
          <StyledHeroText>
            <Breadcrumbs currentPage={BREADCRUMBS_PAGE_TEXT.ADMIN_HOME_PAGE} />

            <CoreTypography sx={sxStyles.heroTitle} variant="h1">
              {school.directory.name}
            </CoreTypography>

            <CoreTypography sx={sxStyles.heroSubtitle}>
              Here you can edit your school profile image and edit the areas of study and majors that students see with
              your school profile.
            </CoreTypography>

            <StyledHeaderActionsWrap>
              <StyledAddAreaBtn onClick={handleOpenAreaModal()}>
                <PlusIcon />
                Add area of study
              </StyledAddAreaBtn>

              <StyledPreviewBtn to={getSchoolDetailsPath(ipedsId)}>
                <PreviewIcon />
                Student view
              </StyledPreviewBtn>
            </StyledHeaderActionsWrap>
          </StyledHeroText>

          <StyledHeroImg>
            {schoolPhotoUrl ? <img src={schoolPhotoUrl} alt="school-photo" /> : null}

            <StyledUpdateImageBtn onClick={() => setShowEditPhoto(!showEditPhoto)}>
              <ImageIcon />
              Update image
            </StyledUpdateImageBtn>
          </StyledHeroImg>
        </StyledHeroSection>
      </CoreBox>

      <CoreBox sx={sxStyles.contentWrap}>
        <Container>
          {areas.map((area) => {
            const currentArea = classifications.find((classification) => {
              if (classification.cipCode === +area) {
                return classification.cipTitle;
              }
              return '';
            });

            const areaPrograms = school.completions.reduce((accumulator, currentValue) => {
              if (area === Math.floor(+currentValue.cipCode)) {
                accumulator.push(+currentValue.cipCode);
              }

              return accumulator;
            }, [] as number[]);

            return (
              <Suspense key={area} fallback={null}>
                <AreasAccordion
                  key={area}
                  areaTitle={currentArea?.cipTitle || ''}
                  areaCipCode={area}
                  areaPrograms={areaPrograms}
                  onOpenEditAreaModal={handleOpenAreaModal(area)}
                />
              </Suspense>
            );
          })}
        </Container>

        {areas.length === 0 ? (
          <StyledEmptyStateText>
            <Suspense fallback={null}>
              <NotepadIcon />
            </Suspense>

            <CoreTypography variant="h1">Looks like there are no Areas of study defined for you school.</CoreTypography>

            <StyledAddAreaBtn onClick={handleOpenAreaModal()}>
              <PlusIcon />
              Add area of study
            </StyledAddAreaBtn>
          </StyledEmptyStateText>
        ) : null}
      </CoreBox>

      <IPEDS />

      <EditImage school={school} showEditPhoto={showEditPhoto} setShowEditPhoto={setShowEditPhoto} />

      {isOpenAreaModal ? (
        <Suspense fallback={null}>
          <AreaModal
            isOpenAreaModal={isOpenAreaModal}
            onCloseAreaModal={handleCloseAreaModal}
            editModeCipCode={editModeCipCode}
          />
        </Suspense>
      ) : null}
    </>
  );
};
